import React, { useState } from "react";
import { BankIcon } from "icons/payments/BankIcon";
import { DeleteCard } from "icons/dashboard/DeleteCard";
import Reconnect from "icons/dashboard/Reconnect";
import { CrownCard } from "icons/dashboard/CrownCard";
import CustomTooltip from "components/ui-component/CustomTooltip";
import PaymentMethodActionsMenu from "./PaymentMethodActionsMenu";
import { CrownMenuIcon } from "icons/payments/CrownMenuIcon";
import { DeleteMenuIcon } from "icons/payments/DeleteMenuIcon";
import PlaidBankIconForm from "../../pages/paymentMethod/PlaidBankIconForm";
import { PaymentGateway } from "types/payments";
import { getBankLogo, maskString } from "utils/HelperFunctions";
import { useTid } from "utils/testid";

type Val = any;

interface Props {
  val: Val;
  idx: number;
  onDelete: () => void;
  onReconnect: () => void;
  onPlaidReconnect: () => void;
  onDefault: () => void;
  isMobile?: boolean;
}

const PaymentMethodBankCard: React.FC<Props> = ({
  val,
  idx,
  onDelete,
  onReconnect,
  onPlaidReconnect,
  onDefault,
  isMobile = false,
}) => {
  const tid = useTid();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const bankId = val?.id || val?.payment_card_id || val?.account_number || idx;
  const bankLabel = `${val?.bank_name || "Bank"} account ${maskString(
    val?.account_number
  )}`;
  const unlinkedStatus =
    val?.is_connected === "N" &&
    (val?.gateway_name === PaymentGateway?.STRIPE || val?.pc_id > 0);

  return (
    <div
      role="group"
      aria-label={bankLabel}
      data-testid={tid(`bank-method-${bankId}`)}
      className={`relative ${isMenuOpen ? "z-[5]" : ""} portalBackgroupBankWallet group rounded-[16px] shadow-[0_12px_12px_rgba(15,38,92,0.17)] h-[200px] xl:rounded-[12px] xl:shadow-none xl:h-[150px] xl:hover:!opacity-70 w-[350px] max-w-full xl:w-[260px] p-4 animate__animated animate__fadeIn`}
    >
      {!val?.available && (
        <div className="absolute top-0 left-0 w-full h-full rounded-[16px] xl:rounded-[12px] z-[1] portalBackgroupBankWalletUnavailable"></div>
      )}

      <div className="flex rounded-tl-[16px] xl:rounded-tl-[12px] absolute z-[2] top-0 left-0">
        {unlinkedStatus ? (
          <>
            <div className="bg-red w-3 xl:group-hover:opacity-30 rounded-tl-[16px] xl:rounded-tl-[12px] "></div>
            <div
              className="xl:group-hover:opacity-30 bg-red  py-[2px] pr-[15px] text-white text-xs  font-medium"
              style={{ clipPath: "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)" }}
            >
              Unlinked
            </div>
          </>
        ) : (
          <div></div>
        )}

        {!val?.available && (
          <>
            {!unlinkedStatus ? (
              <div className="bg-yellow w-3 rounded-tl-[16px] xl:rounded-tl-[12px] xl:group-hover:opacity-30"></div>
            ) : null}

            <div
              style={{
                clipPath: unlinkedStatus
                  ? "polygon(8% 0, 100% 0%, 90% 99%, 0% 100%)"
                  : "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
              }}
              className={`xl:group-hover:opacity-30 bg-yellow text-white text-xs leading-normal flex items-center font-medium ${
                !unlinkedStatus ? "pr-[15px]" : "py-[2px] px-[15px] ml-[-11px]"
              }`}
            >
              Unavailable
            </div>
          </>
        )}

        {val?.default_profile === "Y" && val?.available && !unlinkedStatus ? (
          <>
            {!unlinkedStatus ? (
              <div className="bg-iconObscure w-3 rounded-tl-[16px] xl:rounded-tl-[12px] xl:group-hover:opacity-30"></div>
            ) : null}
            <div
              className={`xl:group-hover:opacity-30 bg-iconObscure  text-white text-xs leading-normal flex items-center font-medium ${
                !unlinkedStatus ? "pr-[15px]" : "py-[2px] px-[15px] ml-[-11px]"
              }`}
              style={{
                clipPath: unlinkedStatus
                  ? "polygon(8% 0, 100% 0%, 90% 99%, 0% 100%)"
                  : "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
              }}
            >
              Default
            </div>
          </>
        ) : null}
      </div>

      <div className="flex justify-center items-center flex-col gap-1.5 h-full pt-2.5">
        <div className="text-sm leading-normal font-normal text-txtPrimary flex whitespace-nowrap">
          <p title={val?.bank_name} className="max-w-[250px] truncate">
            {val?.bank_name || "Test Bank"}
          </p>
        </div>
        <div>
          {getBankLogo({ bankName: val?.bank_name, type: "big" }) || (
            <BankIcon aria-hidden="true" />
          )}
        </div>
        <div className="text-lg leading-normal font-bold text-txtParagraph">
          {maskString(val?.account_number)}
        </div>
      </div>

      <PaymentMethodActionsMenu
        testId={`bank-method-${bankId}`}
        triggerLabel={`Actions for ${bankLabel}`}
        tone="onLight"
        onOpenChange={setIsMenuOpen}
        actions={[
          ...(!val?.isExpired &&
          val?.default_profile !== "Y" &&
          val?.available &&
          !unlinkedStatus
            ? [
                {
                  key: "set-default",
                  label: "Set as Default",
                  icon: <CrownMenuIcon />,
                  onClick: onDefault,
                },
              ]
            : []),
          ...(unlinkedStatus && val?.available
            ? [
                val?.gateway_name === PaymentGateway?.STRIPE
                  ? {
                      key: "reconnect",
                      label: "Reconnect Bank",
                      icon: (
                        <Reconnect width={16} height={16} viewBox="7 7 20 20" />
                      ),
                      onClick: onReconnect,
                    }
                  : {
                      key: "reconnect",
                      node: (
                        <PlaidBankIconForm
                          editData={val}
                          index={idx}
                          variant="menuItem"
                          onOldCardDataGet={onPlaidReconnect}
                        />
                      ),
                    },
              ]
            : []),
          {
            key: "delete",
            label: "Delete Bank",
            icon: <DeleteMenuIcon />,
            onClick: onDelete,
            isDestructive: true,
            dividerBefore:
              val?.available &&
              (unlinkedStatus ||
                (!val?.isExpired && val?.default_profile !== "Y")),
          },
        ]}
      />

      <div className="absolute bottom-2 z-[2] right-2 cursor-pointer hidden gap-2 xl:group-hover:flex">
        <div
          role="button"
          tabIndex={0}
          aria-label={`Delete ${bankLabel}`}
          data-testid={tid(`bank-method-${bankId}-delete-button`)}
          onClick={onDelete}
        >
          <CustomTooltip
            maxwidth="430px"
            place="top"
            id={`bank-delete-${bankId}`}
            content={`<div class="text-xs leading-[18px] font-normal">Delete</div>`}
            disabled={isMobile}
          >
            <DeleteCard
              id={`bank-delete-${bankId}`}
              height="34"
              width="34"
              aria-hidden="true"
            />
          </CustomTooltip>
        </div>
        {unlinkedStatus && val?.available && (
          <>
            {val?.gateway_name === PaymentGateway?.STRIPE ? (
              <div
                role="button"
                tabIndex={0}
                aria-label={`Reconnect ${bankLabel}`}
                data-testid={tid(`bank-method-${bankId}-reconnect-button`)}
                onClick={onReconnect}
              >
                <CustomTooltip
                  maxwidth="430px"
                  place="top"
                  id={`bank-reconnect-${bankId}`}
                  content={`<div class="text-xs leading-[18px] font-normal">Reconnect</div>`}
                  disabled={isMobile}
                >
                  <Reconnect
                    id={`bank-reconnect-${bankId}`}
                    height="34"
                    width="34"
                    aria-hidden="true"
                  />
                </CustomTooltip>
              </div>
            ) : (
              <PlaidBankIconForm
                editData={val}
                key={bankId}
                index={idx}
                onOldCardDataGet={onPlaidReconnect}
              />
            )}
          </>
        )}
        {!val?.isExpired &&
        val?.default_profile !== "Y" &&
        val?.available &&
        !unlinkedStatus ? (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Set ${bankLabel} as default`}
            data-testid={tid(`bank-method-${bankId}-default-button`)}
            onClick={onDefault}
          >
            <CustomTooltip
              maxwidth="430px"
              place="top"
              id={`bank-default-${bankId}`}
              content={`<div class="text-xs leading-[18px] font-normal w-[80px]">Set as default</div>`}
              disabled={isMobile}
            >
              <CrownCard
                id={`bank-default-${bankId}`}
                height="34"
                width="34"
                aria-hidden="true"
              />
            </CustomTooltip>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentMethodBankCard;
